import React from "react";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, StatCard } from "@/components/common/DashboardBits";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ClipboardList, User, UserCheck, AlertTriangle } from "lucide-react";

export default function FieldOverview() {
  const { user } = useAuth();
  const { t } = useLang();
  const stats = useDashboardStats();

  const manager = stats?.manager;
  const isApproved = stats?.is_approved;

  return (
    <div>
      <PageHeader title={`${t("welcome_back")}, ${user?.name || ""}`} subtitle="Your assigned property visits" />

      {/* Reporting manager / approval banner */}
      {stats && (
        manager ? (
          <Card className="border border-emerald-200 bg-emerald-50/60 mb-6 max-w-2xl" data-testid="field-manager-card">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-800">Reporting Manager</div>
                <div className="font-heading text-lg font-bold text-slate-950 mt-0.5" data-testid="field-manager-name">
                  {manager.name}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
                  {manager.phone && <span data-testid="field-manager-phone">📞 {manager.phone}</span>}
                  {manager.email && <span data-testid="field-manager-email">✉️ {manager.email}</span>}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : !isApproved ? (
          <Card className="border border-amber-200 bg-amber-50/60 mb-6 max-w-2xl" data-testid="field-pending-card">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-amber-800">Awaiting approval</div>
                <div className="text-sm text-slate-700 mt-0.5">
                  Your account is waiting for a territory manager to review and add you to their team.
                  You'll be able to accept leads once you're approved.
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard testId="stat-pending" label={t("pending")} value={stats?.pending ?? 0} tone="amber" />
        <StatCard testId="stat-visited" label={t("visited")} value={stats?.visited ?? 0} tone="green" />
        <StatCard testId="stat-submitted" label={t("submitted")} value={stats?.submitted ?? 0} tone="green" />
        <StatCard testId="stat-total" label={t("total_leads")} value={stats?.total_leads ?? 0} />
      </div>

      <Card className="border border-slate-200 max-w-2xl" data-testid="field-quick-actions">
        <CardContent className="p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Quick actions</div>
          <div className="text-sm text-slate-600 mt-2">
            Open your assigned leads to capture boundaries, dimensions, bylaws and site photos.
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/field/visits" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-950 hover:bg-slate-800 text-white rounded-md text-sm" data-testid="field-goto-leads">
              <ClipboardList className="h-4 w-4" /> {t("my_leads")}
            </Link>
            <Link to="/field/profile" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-md text-sm" data-testid="field-goto-profile">
              <User className="h-4 w-4" /> {t("profile")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
